import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, Badge, requestBadgeMap } from '../../components/UI';
import { KpiCard, SectionIcon } from '../../components/StudentUI';
import { fmtDate, fmtPct } from '../../utils/format';

const OPEN_REQUEST_STATUSES = new Set(['new', 'pending', 'in_review']);

function firstName(name = '') { return name.split(' ')[0] || name; }

// Records come back newest-first (ORDER BY session_date DESC). Counts
// consecutive present/late sessions from the most recent backwards; an MC
// is excused so it's skipped rather than breaking the streak, but an actual
// absence stops the count.
function attendanceStreak(records = []) {
  let streak = 0;
  for (const r of records) {
    if (r.status === 'present' || r.status === 'late') streak++;
    else if (r.status === 'mc') continue;
    else break;
  }
  return streak;
}

const CIRCUMFERENCE = 2 * Math.PI * 26;
function ringOffset(pct) {
  const clamped = Math.max(0, Math.min(100, pct));
  return CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE;
}

export default function StudentDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [academic, setAcademic] = useState(null);
  const [activities, setActivities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a, ac, act, req, srv] = await Promise.all([
          api.get('/me/profile'),
          api.get('/me/attendance'),
          api.get('/me/academic'),
          api.get('/me/activities'),
          api.get('/me/requests'),
          api.get('/me/esurveys'),
        ]);
        if (cancelled) return;
        setProfile(p.data.data);
        setAttendance(a.data.data);
        setAcademic(ac.data.data);
        setActivities(act.data.data || []);
        setRequests(req.data.data || []);
        setSurveys(srv.data.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleTakeSurvey = async link => {
    window.open(link.survey_url, '_blank', 'noopener,noreferrer');
    try {
      await api.post(`/me/esurveys/${link.id}/opened`);
      setSurveys(list => list.map(l => (l.id === link.id ? { ...l, opened_at: new Date().toISOString() } : l)));
    } catch {
      // Non-blocking — worst case the card still shows "Take survey" next visit.
    }
  };

  if (loading) return <div className="stu-page"><Spinner /></div>;

  const s = attendance?.summary;
  const totalSessions = s ? Number(s.total_sessions) || 0 : 0;
  const attendanceRate = s && totalSessions > 0
    ? Math.round((((Number(s.present) + Number(s.late) + Number(s.mc || 0)) - Number(s.absent || 0)) / totalSessions) * 100)
    : null;

  const latestScore = academic?.records?.[0];
  // Weighted by number of assessments per subject, not a flat average of subject averages.
  const totalAssessments = academic?.summary?.reduce((sum, r) => sum + Number(r.assessments), 0) || 0;
  const overallAvg = totalAssessments > 0
    ? (academic.summary.reduce((sum, r) => sum + Number(r.avg_pct) * Number(r.assessments), 0) / totalAssessments).toFixed(1)
    : null;

  const recent = [
    ...activities.slice(0, 2).map(a => ({ date: a.log_date, cat: 'Activity log', desc: a.description, dot: 'var(--sms-stat-cyan)' })),
    ...(academic?.records || []).slice(0, 2).map(a => ({
      date: a.assessment_date, cat: 'Academic', dot: 'var(--sms-stat-violet)',
      desc: `Score recorded: ${a.subject}${a.intake_code ? ` (${a.intake_code})` : ''} — ${a.assessment} (${a.score}/${a.max_score}${a.grade ? `, Grade ${a.grade}` : ''})`,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  const openRequestCount = requests.filter(r => OPEN_REQUEST_STATUSES.has(r.status)).length;
  const recentRequests = [...requests].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 3);

  const streak = attendanceStreak(attendance?.records);
  // academic.records comes back newest-first; take the last 5 and put them
  // back in chronological order so the trend reads left-to-right like a timeline.
  const scoreTrend = (academic?.records || [])
    .slice(0, 5).reverse()
    .map(a => ({ pct: Math.round((Number(a.score) / Number(a.max_score)) * 100), label: a.assessment }));

  return (
    <>
      <div className="stu-topbar stu-topbar-glow">
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 10 }}>
          <div>
            <div className="stu-topbar-title"><span className="stu-hero-wave">👋</span> Welcome back, {firstName(user?.full_name)}</div>
            <div className="stu-topbar-sub">{profile?.intake ? `Intake ${profile.intake} · ` : ''}{profile?.status}</div>
          </div>
          {streak > 1 && (
            <div className="stu-streak-chip">
              <span style={{ color: 'var(--sms-stat-amber)' }}>🔥</span> {streak}-session attendance streak
            </div>
          )}
        </div>
      </div>
      <div className="stu-page">
        {surveys.length > 0 && (
          <div className="stu-card">
            <div className="stu-card-head">
              <h3 className="d-flex align-items-center"><SectionIcon icon="bi-clipboard-check" color="var(--sms-stat-amber)" />Pending Surveys</h3>
              <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                {surveys.filter(s => !s.opened_at).length} of {surveys.length} need action
              </span>
            </div>
            <div className="stu-card-body">
              <div className="stu-req-grid">
                {surveys.map(s => (
                  <div className="stu-req-tile" key={s.id}>
                    <div className="d-flex align-items-center justify-content-between" style={{ gap: 8, marginBottom: 4 }}>
                      <span className="stu-activity-cat">{s.module_name || s.module_code}</span>
                      {/* Full-contrast text throughout — "done" is conveyed via this
                          verified pill pair (stu-pill.ok), not by dimming opacity,
                          which would drag the module-name text below WCAG AA. */}
                      {s.opened_at && <span className="stu-pill ok">Opened <i className="bi bi-check-lg" /></span>}
                    </div>
                    <div className="stu-activity-desc">{s.course_name || s.course_code} · {s.intake_code}</div>
                    <div style={{ marginTop: 10 }}>
                      {s.opened_at ? (
                        <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '6px 12px', fontSize: 12 }}
                          onClick={() => handleTakeSurvey(s)}>
                          Open again <i className="bi bi-box-arrow-up-right" />
                        </button>
                      ) : (
                        <button type="button" className="sms-btn sms-btn-primary" style={{ padding: '6px 12px', fontSize: 12 }}
                          onClick={() => handleTakeSurvey(s)}>
                          Take survey <i className="bi bi-box-arrow-up-right" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="stu-stat-row">
          <div className="stu-stat-card stu-ring-card">
            <div className="stu-stat-body">
              <div className="stu-ring-wrap">
                <svg width="60" height="60" viewBox="0 0 60 60">
                  <circle className="stu-ring-track" cx="30" cy="30" r="26" />
                  <circle className="stu-ring-fill" cx="30" cy="30" r="26"
                    stroke={attendanceRate !== null ? 'var(--sms-dot-present)' : 'var(--sms-sur2)'}
                    style={{ '--ring-offset': attendanceRate !== null ? ringOffset(attendanceRate) : CIRCUMFERENCE }} />
                </svg>
                <div className="stu-ring-num">{attendanceRate !== null ? `${fmtPct(attendanceRate)}` : '—'}</div>
              </div>
              <div>
                <div className="stu-stat-label">Attendance</div>
                <div className="stu-stat-caption">{s ? `${s.present || 0} present · ${s.late || 0} late · ${s.absent || 0} absent · ${s.mc || 0} MC` : 'No sessions recorded yet'}</div>
              </div>
            </div>
          </div>
          <KpiCard label="Latest Grade" value={latestScore?.grade || '—'} icon="bi-mortarboard" color="var(--sms-stat-blue)"
            caption={latestScore ? `${latestScore.subject}${latestScore.intake_code ? ` (${latestScore.intake_code})` : ''} · ${latestScore.assessment}` : 'No assessments yet'} />
          <KpiCard label="Overall Average" value={overallAvg !== null ? overallAvg : '—'} icon="bi-graph-up" color="var(--sms-stat-violet)"
            caption={academic?.summary?.length ? `across ${academic.summary.length} subject(s)` : 'No assessments yet'} />
          <KpiCard label="Enrolled Since" icon="bi-calendar3" color="var(--sms-stat-cyan)"
            value={<span style={{ fontSize: 20 }}>{fmtDate(profile?.enrollment_date)}</span>}
            caption={`Guiding language: ${profile?.guiding_language || '—'}`} />
          <KpiCard label="Open Requests" value={openRequestCount} icon="bi-envelope-open" color="var(--sms-stat-amber)"
            caption={requests.length ? `${requests.length} total submitted` : 'No requests yet'} />
        </div>

        {scoreTrend.length > 1 && (
          <div className="stu-card">
            <div className="stu-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-bar-chart-line" color="var(--sms-stat-violet)" />Score trend</h3><span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>Last {scoreTrend.length} assessments</span></div>
            <div className="stu-card-body">
              <div className="stu-trend-row">
                {scoreTrend.map((t, i) => (
                  <div className="stu-trend-col" key={i} title={`${t.label}: ${fmtPct(t.pct)}`}>
                    <div className="stu-trend-bar" style={{ height: `${Math.max(t.pct, 4)}%`, animationDelay: `${i * 0.05}s` }} />
                    <div className="stu-trend-lbl">{t.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="stu-card">
          <div className="stu-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-activity" color="var(--sms-stat-cyan)" />Recent activity</h3></div>
          <div className="stu-card-body">
            {recent.length === 0 && <div className="stu-empty">Nothing recorded yet.</div>}
            {recent.length > 0 && (
              <div className="stu-timeline">
                {recent.map((r, i) => (
                  <div className="stu-t-item" key={i} style={{ '--dot': r.dot, animationDelay: `${i * 0.05}s` }}>
                    <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                      <span className="stu-activity-cat">{r.cat}</span>
                      <span style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{fmtDate(r.date)}</span>
                    </div>
                    <div className="stu-activity-desc">{r.desc}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="stu-card">
          <div className="stu-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-envelope-open" color="var(--sms-stat-amber)" />Student Requests</h3>
            <button type="button" className="stu-link-pill" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => navigate('/student/requests')}>
              View all
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="stu-card-body">
            {recentRequests.length === 0 && <div className="stu-empty">You haven't submitted any requests yet.</div>}
            {recentRequests.length > 0 && (
              <div className="stu-req-grid">
                {recentRequests.map(r => (
                  <div className="stu-req-tile" key={r.id}>
                    <div className="d-flex align-items-center justify-content-between" style={{ gap: 8, marginBottom: 4 }}>
                      <span className="stu-activity-cat">{r.request_type}</span>
                      <Badge value={r.status} map={requestBadgeMap} />
                    </div>
                    {r.description && <div className="stu-req-clamp stu-activity-desc">{r.description}</div>}
                    <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 6 }}>{fmtDate(r.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
